import { ChangeDetectionStrategy, Component, computed, inject, OnInit, Signal, signal } from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import { Address } from '../../core/models/address.model';
import { Appointment } from '../../core/models/appointment.model';
import { CONTACT_OUTCOME_KINDS, Interaction, OUTCOME_KINDS, OutcomeKind } from '../../core/models/interaction.model';
import { AddressService } from '../../core/services/address.service';
import { AppointmentService } from '../../core/services/appointment.service';
import { AuthService } from '../../core/services/auth.service';
import { ClockService } from '../../core/services/clock.service';
import { InteractionService } from '../../core/services/interaction.service';
import { TranslationService } from '../../core/services/translation.service';
import { UserStorageService } from '../../core/services/user-storage.service';
import { WorkContextService } from '../../core/services/work-context.service';
import { CardContentComponent } from '../../shared/components/card/card-content.component';
import { CardHeaderComponent } from '../../shared/components/card/card-header.component';
import { CardTitleComponent } from '../../shared/components/card/card-title.component';
import { CardComponent } from '../../shared/components/card/card.component';
import { IconComponent } from '../../shared/components/icon/icon.component';
import { OutcomeChipComponent } from '../../shared/components/outcome-chip/outcome-chip.component';
import { SpinnerComponent } from '../../shared/components/spinner/spinner.component';
import { DateTimezonePipe } from '../../shared/pipes/date-timezone.pipe';
import { RelativeTimePipe } from '../../shared/pipes/relative-time.pipe';
import { TranslatePipe } from '../../shared/pipes/translate.pipe';
import { ACTIVE_WORK_CONTEXT_KEY } from './active-work-context';

/**
 * The canvasser's day: today's own interactions with a per-outcome tally,
 * plus follow-ups that are due. Becomes a bottom-nav tab in phase 3.
 */
@Component({
  selector: 'app-my-day',
  standalone: true,
  imports: [
    RouterLink,
    ReactiveFormsModule,
    FormlyModule,
    CardComponent,
    CardHeaderComponent,
    CardTitleComponent,
    CardContentComponent,
    IconComponent,
    SpinnerComponent,
    OutcomeChipComponent,
    DateTimezonePipe,
    RelativeTimePipe,
    TranslatePipe,
  ],
  templateUrl: './my-day.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './my-day.component.css',
})
export class MyDayComponent implements OnInit {
  private interactionService = inject(InteractionService);
  private addressService = inject(AddressService);
  private appointmentService = inject(AppointmentService);
  private authService = inject(AuthService);
  private workContextService = inject(WorkContextService);
  private userStorage = inject(UserStorageService);
  private translate = inject(TranslationService);

  readonly now = inject(ClockService).now;

  today!: Signal<Interaction[]>;
  loading!: Signal<boolean>;
  followUps!: Signal<Interaction[]>;
  upcomingAppointments!: Signal<Appointment[]>;
  addresses!: Signal<Address[]>;

  readonly outcomeKinds = OUTCOME_KINDS;

  addressById = computed(() => new Map(this.addresses().map((a) => [a.id, a])));

  tally = computed(() => {
    const counts = new Map<OutcomeKind, number>();
    for (const i of this.today()) counts.set(i.outcome_kind, (counts.get(i.outcome_kind) ?? 0) + 1);
    return counts;
  });

  doors = computed(() => this.today().length);
  contacts = computed(() => this.today().filter((i) => CONTACT_OUTCOME_KINDS.includes(i.outcome_kind)).length);
  leads = computed(() => this.today().filter((i) => i.outcome_kind === 'lead').length);

  /** "Active campaign" picker — a per-device preference (design doc §6.5), not synced state. */
  campaignForm = new FormGroup({});
  campaignModel: { workContextId: string } = { workContextId: '' };
  campaignFields: FormlyFieldConfig[] = [];
  showCampaignPicker = signal(false);

  async ngOnInit(): Promise<void> {
    const userId = this.authService.getCurrentUserId() ?? '';
    const start = new Date(this.now());
    start.setHours(0, 0, 0, 0);
    const end = new Date(start);
    end.setDate(end.getDate() + 1);

    const todayQuery = this.interactionService.createQueryForUserBetween(userId, start.getTime(), end.getTime());
    this.today = todayQuery.data;
    this.loading = todayQuery.loading;
    this.followUps = this.interactionService.createQueryFollowUpsDue(userId, end.getTime()).data;
    const tomorrowEnd = end.getTime() + 24 * 60 * 60 * 1000;
    this.upcomingAppointments = this.appointmentService.createQueryUpcomingForUser(userId, tomorrowEnd).data;
    this.addresses = this.addressService.createQuery().data;

    const contexts = await this.workContextService.getActive();
    this.showCampaignPicker.set(contexts.length > 0);
    if (!this.showCampaignPicker()) return;

    const t = (key: string) => this.translate.translate(key);
    this.campaignFields = [
      {
        key: 'workContextId',
        type: 'native-select',
        props: {
          label: t('myDay.activeCampaign.label'),
          showLabel: true,
          options: [
            { value: '', label: t('myDay.activeCampaign.none') },
            ...contexts.map((c) => ({ value: c.id, label: c.name })),
          ],
        },
      },
    ];
    this.campaignModel.workContextId = this.userStorage.getTypedItem<string>(ACTIVE_WORK_CONTEXT_KEY) ?? '';
    this.campaignForm.valueChanges.subscribe(() => {
      this.userStorage.setTypedItem(ACTIVE_WORK_CONTEXT_KEY, this.campaignModel.workContextId);
    });
  }

  addressLabel(addressId: string): string {
    return this.addressById().get(addressId)?.formatted_address ?? '';
  }
}
